import { FC, useState } from 'react'

import {
  Button,
  Cluster,
  Filter,
  FilterBody,
  FilterContent,
  FilterFooter,
  FilterGroup,
  FilterItem,
  FilterList,
  FilterTrigger,
  FilterTriggerButton,
  FilterValue,
  FilterValues,
  Icon,
  Trackable,
  Typography,
} from '@forto-dev/phoenix-ui-core'

import { DateRangeFilter } from '@/features/shipments'
import { DateFilterType } from '@/utils/dateFilters'
import { useLocalisation } from '@/utils/localisation'

import { NOTIFICATION_STATUSES, NOTIFICATION_TYPES, NotificationStatus, NotificationType } from '../domain/notification'
import { useNotificationCenterSearch } from '../hooks/useNotificationCenterSearch'

const translations = {
  'notification-center.filter.duration': 'Duration',
  'notification-center.filter.status': 'Status',
  'notification-center.filter.type': 'Type',
  'notification-center.filter.new': 'New',
  'notification-center.filter.read': 'Read',
  'notification-center.filter.unread': 'Unread',
  'notification-center.filter.deviation': 'Deviation',
  'notification-center.filter.deviationRisk': 'Deviation Risk',
  'notification-center.filter.message': 'Messages',
  'notification-center.filter.update': 'Updates',
  'notification-center.filter.reassurance': 'Reassurance',
}

const filters = ['duration', 'status', 'type'] as const
type NotificationCenterFilter = (typeof filters)[number]

const filterConfig: Record<
  NotificationCenterFilter,
  { type: 'date' | 'multi-select'; options?: NotificationStatus[] | NotificationType[] }
> = {
  duration: {
    type: 'date',
  },
  status: {
    type: 'multi-select',
    options: [...NOTIFICATION_STATUSES],
  },
  type: {
    type: 'multi-select',
    options: [...NOTIFICATION_TYPES],
  },
}

export const NotificationsFilters: FC = () => {
  const { translate } = useLocalisation()
  const [open, setOpen] = useState<boolean>(false)
  const [selectedFilter, setSelectedFilter] = useState<NotificationCenterFilter>()
  const { setSearch, search, clearFilters } = useNotificationCenterSearch()
  const [customDates, setCustomDates] = useState<{ from: Date | undefined; to: Date | undefined }>({
    from: search.durationFrom ? new Date(search.durationFrom) : undefined,
    to: search.durationTo ? new Date(search.durationTo) : undefined,
  })
  const appliedFilters = {
    type: search.type || [],
    status: search.status || [],
    duration: search.duration ? [search.duration] : [],
  }
  const [selectedFilterValues, setSelectedFilterValues] =
    useState<Partial<Record<NotificationCenterFilter, string[]>>>(appliedFilters)
  const filtersSelected = Object.values(appliedFilters).flat().length
  const handleOnFilterItemChange = (value: NotificationCenterFilter) => {
    setSelectedFilter((current) => (current === value ? undefined : value))
  }

  const handleOnFilterValueChange = (filter: NotificationCenterFilter, value: string[]) => {
    setSelectedFilterValues((current) => {
      return { ...current, [filter]: value }
    })
  }

  const getSelectedFilterValues = (filter: NotificationCenterFilter): string | undefined => {
    const appliedFilterValues = appliedFilters[filter]
    if (appliedFilterValues?.length > 0) {
      if (filterConfig[filter].type === 'date') {
        return translate(`billing.filter.${appliedFilterValues[0] as DateFilterType}`)
      } else {
        return (appliedFilterValues as (NotificationStatus | NotificationType)[])
          .map((value) => translations[`notification-center.filter.${value}`])
          .join(', ')
      }
    }
  }
  return (
    <Filter open={open} onOpenChange={setOpen}>
      <FilterTrigger
        startButton={
          <FilterTriggerButton>
            {filtersSelected > 0 ? (
              <Typography color="primary">
                {translate('filters.count_selected', {
                  count: filtersSelected,
                })}
              </Typography>
            ) : (
              <Cluster gap={4} y="center">
                <Typography variant="small">{translate('filters.addFilter')}</Typography>
                <Icon name="Plus" size="sm" color="default" />
              </Cluster>
            )}
          </FilterTriggerButton>
        }
        endButton={
          filtersSelected > 0 && (
            <FilterTriggerButton
              onClick={() => {
                clearFilters()
                setSelectedFilterValues({})
              }}
            >
              <Cluster gap={4} y="center">
                <Typography color="muted">{translate('filters.reset')}</Typography>
                <Icon name="Close" size="xs" />
              </Cluster>
            </FilterTriggerButton>
          )
        }
      />
      <FilterContent>
        <FilterBody>
          <FilterList activeItem={selectedFilter || ''}>
            <FilterGroup label={translate('filters.filters')}>
              {filters.map((filter) => {
                return (
                  <FilterItem
                    key={filter}
                    onSelect={(selectedFilter) => handleOnFilterItemChange(selectedFilter as NotificationCenterFilter)}
                    value={filter}
                    label={translations[`notification-center.filter.${filter}`]}
                    active={selectedFilter === filter}
                    suffix={getSelectedFilterValues(filter)}
                  />
                )
              })}
            </FilterGroup>
          </FilterList>
          {selectedFilter && filterConfig[selectedFilter] && (
            <FilterValues>
              {filterConfig[selectedFilter].type === 'date' && (
                <DateRangeFilter
                  selectedDateFilter={selectedFilterValues.duration?.[0] as DateFilterType}
                  selectedFromDate={customDates.from}
                  selectedToDate={customDates.to}
                  onChange={({ dateFilter, from, to }) => {
                    handleOnFilterValueChange('duration', [dateFilter])
                    setCustomDates({ from, to })
                  }}
                />
              )}
              {filterConfig[selectedFilter].type === 'multi-select' && (
                <>
                  {filterConfig[selectedFilter].options?.map((option) => {
                    const currentValues = (selectedFilterValues[selectedFilter] || []) as string[]
                    return (
                      <Trackable key={option} data-testid={`filters.notification-${selectedFilter}.${option}`}>
                        <FilterValue
                          type="checkbox"
                          name={`notification-${selectedFilter}-filter`}
                          onSelect={() => {
                            let newValues = []
                            if (currentValues.includes(option)) {
                              newValues = currentValues.filter((item) => item !== option)
                            } else {
                              newValues = [...currentValues, option]
                            }
                            handleOnFilterValueChange(selectedFilter, newValues)
                          }}
                          value={option}
                          label={translations[`notification-center.filter.${option}`]}
                          selected={currentValues.includes(option)}
                        />
                      </Trackable>
                    )
                  })}
                </>
              )}
            </FilterValues>
          )}
        </FilterBody>
        {selectedFilter && (
          <FilterFooter>
            <Button
              variant="outline-secondary"
              onClick={() => {
                setSelectedFilter(undefined)
                setSelectedFilterValues(appliedFilters)
                setOpen(false)
              }}
            >
              {translate('filters.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                const newSearchParams = {
                  status: (selectedFilterValues.status as NotificationStatus[]) ?? [],
                  type: (selectedFilterValues.type as NotificationType[]) ?? [],
                  duration: (selectedFilterValues.duration?.[0] as DateFilterType) ?? [],
                  durationFrom: customDates.from ? String(customDates.from) : undefined,
                  durationTo: customDates.to ? String(customDates.to) : undefined,
                }
                setSearch(newSearchParams)
                setSelectedFilter(undefined)
                setOpen(false)
              }}
              disabled={
                Object.values(appliedFilters).flat().length === 0 &&
                Object.values(selectedFilterValues).flat().length === 0
              }
            >
              {translate('filters.apply')}
            </Button>
          </FilterFooter>
        )}
      </FilterContent>
    </Filter>
  )
}
